import { useMemo, useRef } from "react";
import {
  ReactFlow, Background, Controls, Handle, MarkerType, Position,
  type Edge, type Node, type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { NODE_PALETTE, type GraphEdge, type GraphNode } from "../../types/ontology";
import { NodeKindIcon } from "../integrations/NodeKindIcon";
import { useGraphFrame } from "../integrations/ExpandableGraphFrame";
import { cls } from "../../lib/cls";

/* SOTA Project Map — @xyflow/react (28k★, used by Stripe, Linear,
 * Replicate) with custom Apple-style node components.
 *
 * Each node is a real React component, not a canvas blit, so we get:
 *   • brand-grade Phosphor duotone icons that scale crisply
 *   • New York serif labels with hierarchy
 *   • Per-kind colour ring, derived from NODE_PALETTE in types/ontology.ts
 *   • Hover lift + click selection without WebGL plumbing
 *   • Auto-layout via dagre (LR for clarity, can be swapped to TB)
 *   • Real drag, pan, zoom built-in
 *   • Edge labels render natively, no GLSL
 */

interface HyperGraphFlowProps {
  highlightNodeIds?: Set<string>;
  onNodeClick?: (node: GraphNode) => void;
  height?: number;
  /** Layout direction — LR (left-right) feels Apple-keynote; TB is
   *  the classic top-down for hierarchical reading. */
  direction?: "LR" | "TB" | "BT" | "RL";
  /** When set, the graph is filtered to a subgraph in the N-hop
   *  neighbourhood of the focused / highlighted set — answers the
   *  cockpit's question "what is the agent touching *right now*?". */
  focusNodeIds?: ReadonlyArray<string>;
  /** Subgraph radius (default 2 hops out from any focused node). */
  subgraphHops?: number;
}

interface NodeData extends Record<string, unknown> {
  label: string;
  kind: GraphNode["kind"];
  active: boolean;
  /** Typed subtype — SKU.category, Room.roomKind, Zone.zoneKind. */
  subtype: string;
}

function FlowNode({ data, selected }: { data: NodeData; selected?: boolean }): JSX.Element {
  const palette = NODE_PALETTE[data.kind];
  return (
    <div className="group">
      <Handle type="target" position={Position.Left}  className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-ink-300 !border-ink-300" />
      <div
        className={cls(
          "flex items-center gap-2.5 px-3 py-2 rounded-xl bg-cream ring-1 transition-all cursor-pointer",
          "shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:-translate-y-0.5 group-hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.10)]",
          (selected || data.active) && "!ring-2 !ring-foyr shadow-[0_4px_14px_-2px_rgba(201,92,26,0.20)]",
        )}
        style={{
          minWidth: 160,
          // Per-kind colour: ring + glyph background
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          ["--node-color" as never]: palette.hex,
          boxShadow: !selected && !data.active
            ? `0 0 0 1px ${palette.hex}25, 0 1px 2px rgba(0,0,0,0.04)`
            : undefined,
        }}
      >
        <span
          className="grid place-items-center w-8 h-8 rounded-lg shrink-0"
          style={{ background: `${palette.hex}15`, color: palette.hex }}
        >
          <NodeKindIcon kind={data.kind} subtype={data.subtype} size={18} />
        </span>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-wider font-mono opacity-60" style={{ color: palette.hex }}>
            {data.kind}
          </div>
          <div className="font-newyork text-[13px] font-semibold text-ink-950 leading-tight tracking-tight truncate" style={{ maxWidth: 220 }}>
            {data.label}
          </div>
        </div>
      </div>
    </div>
  );
}

const nodeTypes: NodeTypes = { hgm: FlowNode as unknown as NodeTypes[string] };

/* ─── Dagre auto-layout — computes (x,y) per node so the diagram
 *     is readable on first paint with no manual placement. ─── */
function layoutWithDagre(
  nodes: GraphNode[],
  edges: GraphEdge[],
  direction: HyperGraphFlowProps["direction"] = "LR",
): { nodes: Node[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: direction, nodesep: 50, ranksep: 100, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  const NODE_W = 240;
  const NODE_H = 56;
  for (const n of nodes) g.setNode(n.id, { width: NODE_W, height: NODE_H });
  for (const e of edges) g.setEdge(e.source, e.target);
  dagre.layout(g);

  const flowNodes: Node[] = nodes.map((n) => {
    const pos = g.node(n.id);
    const subtype = String(
      n.attrs?.["category"] ?? n.attrs?.["roomKind"] ?? n.attrs?.["zoneKind"] ?? "",
    );
    return {
      id: n.id,
      type: "hgm",
      position: { x: (pos?.x ?? 0) - NODE_W / 2, y: (pos?.y ?? 0) - NODE_H / 2 },
      data: { label: n.label, kind: n.kind, active: false, subtype } as NodeData,
      draggable: true,
    };
  });
  const flowEdges: Edge[] = edges.map((e) => {
    const causal = /RULE_FIRED_FROM|RULE_RECOMMENDS|BLOCKED_BY|DEPENDS_ON/.test(e.kind);
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: "smoothstep",
      animated: causal,
      label: e.kind.replace(/_/g, " ").toLowerCase(),
      labelStyle: { fontSize: 9, fill: "#7a766f", fontFamily: "ui-monospace, monospace" },
      labelBgStyle: { fill: "#f6f1e7" },
      labelBgPadding: [3, 1] as [number, number],
      labelBgBorderRadius: 6,
      markerEnd: { type: MarkerType.ArrowClosed, color: causal ? "#C95C1A" : "#94908a", width: 14, height: 14 },
      style: { stroke: causal ? "#C95C1A" : "#94908a", strokeWidth: causal ? 1.6 : 1.1 },
    };
  });
  return { nodes: flowNodes, edges: flowEdges };
}

export function HyperGraphFlow({
  highlightNodeIds,
  onNodeClick,
  height = 620,
  direction = "LR",
  focusNodeIds,
  subgraphHops = 2,
}: HyperGraphFlowProps): JSX.Element {
  const graphRef = useRef(getActiveProjectGraph());
  const g = getActiveProjectGraph();
  graphRef.current = g;

  const { nodes, edges } = useMemo(() => {
    // In-context subgraph filter: if a focus set is provided, BFS
    // out `subgraphHops` hops and keep only those nodes + connecting
    // edges. Wrong-typing = wrong alert; we'd rather show fewer
    // nodes that all belong than a wall of distant context.
    let baseNodes = [...g.nodes];
    let baseEdges = [...g.edges];

    if (focusNodeIds && focusNodeIds.length > 0) {
      const adj = new Map<string, Set<string>>();
      for (const e of baseEdges) {
        if (!adj.has(e.source)) adj.set(e.source, new Set());
        if (!adj.has(e.target)) adj.set(e.target, new Set());
        adj.get(e.source)!.add(e.target);
        adj.get(e.target)!.add(e.source);
      }
      const keep = new Set<string>(focusNodeIds);
      let frontier = new Set<string>(focusNodeIds);
      for (let hop = 0; hop < subgraphHops; hop++) {
        const next = new Set<string>();
        for (const id of frontier) {
          for (const nb of adj.get(id) ?? []) if (!keep.has(nb)) next.add(nb);
        }
        for (const id of next) keep.add(id);
        frontier = next;
        if (next.size === 0) break;
      }
      baseNodes = baseNodes.filter((n) => keep.has(n.id));
      baseEdges = baseEdges.filter((e) => keep.has(e.source) && keep.has(e.target));
    }

    const laid = layoutWithDagre(baseNodes, baseEdges, direction);
    const set = highlightNodeIds ?? new Set<string>();
    return {
      nodes: laid.nodes.map((n) => set.size === 0 || set.has(n.id)
        ? { ...n, data: { ...(n.data as NodeData), active: set.has(n.id) } }
        : { ...n, style: { opacity: 0.45 } }),
      edges: laid.edges,
    };
  }, [g, direction, highlightNodeIds, focusNodeIds, subgraphHops]);

  const frame = useGraphFrame();

  return (
    <div className="rounded-2xl border border-border/60 overflow-hidden bg-[radial-gradient(circle_at_30%_30%,rgba(201,92,26,0.04),transparent_70%)]" style={{ height }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18 }}
        minZoom={0.2}
        maxZoom={2.4}
        panOnDrag
        panOnScroll={false}
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_e, n) => {
          const node = graphRef.current.nodes.find((x) => x.id === n.id);
          if (node && onNodeClick) onNodeClick(node);
          frame?.setSelectedNodeId(n.id);
        }}
      >
        <Background gap={22} size={1} color="#ece7dc" />
        <Controls showInteractive={false} className="!shadow-sm" />
      </ReactFlow>
    </div>
  );
}
